import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Building, Search, Trash2, Edit, Plus, Download, Upload, Loader2, ArrowLeft, 
  ShieldX, MapPin, Pill, FlaskConical, X, Handshake, Globe, Link
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL Import Dialog Component
function ImportFromURLDialog({ open, onClose, onImport, importing, title }) {
  const [url, setUrl] = useState("");
  
  const handleImport = () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    onImport(url.trim());
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Import {title} from Website
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Paste the pharmaceutical company's website URL and we'll automatically extract pipeline, focus areas, key drugs, and partnership information.
          </p>
          <div className="space-y-2">
            <Label>Website URL</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://pharma-company.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importing || !url.trim()}>
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Extract Data
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const INDICATION_OPTIONS = [
  "oncology", "rare diseases", "immunology", "neurology", "cardiology",
  "respiratory", "infectious diseases", "vaccines", "ophthalmology", "dermatology"
];

const MODALITY_OPTIONS = [
  "small molecule", "biologics", "cell therapy", "gene therapy", "mRNA",
  "antibody-drug conjugates", "radioligand therapy", "vaccines"
];

const CONTACT_APPROACHES = [
  { value: "bd_team", label: "BD Team" },
  { value: "scientists", label: "Scientists" },
  { value: "both", label: "Both BD + Scientists" },
  { value: "licensing_team", label: "Licensing Team" }
];

export default function AdminPharma() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [pharmaList, setPharmaList] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  
  const [pharmaDialog, setPharmaDialog] = useState({ open: false, pharma: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, pharma: null });
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [editTab, setEditTab] = useState("basic");
  const [pharmaForm, setPharmaForm] = useState({
    name: "", headquarters: "", website: "", description: "",
    indication_focus: [], modality_focus: [], key_drugs: [],
    pipeline_stage: "", external_innovation_score: 50, innovation_approach: "",
    annual_rd_spend: "", recent_acquisitions: [], startup_partnerships: [],
    best_contact_approach: "bd_team", contact_notes: "",
    deal_activity_score: 50, startup_friendly_score: 50
  });

  useEffect(() => { checkAccess(); }, []);
  useEffect(() => { if (isAdmin) fetchPharma(); }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) {
      const t = setTimeout(fetchPharma, 300);
      return () => clearTimeout(t);
    }
  }, [search, isAdmin]);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) { navigate("/auth"); return; }
      setUser(await userRes.json());
      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      setIsAdmin(adminRes.ok ? (await adminRes.json()).is_admin : false);
    } catch { navigate("/auth"); }
    finally { setLoading(false); }
  };

  const fetchPharma = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pharma`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        let filtered = data.pharma_companies;
        if (search) {
          const s = search.toLowerCase();
          filtered = filtered.filter(p => p.name?.toLowerCase().includes(s) || p.headquarters?.toLowerCase().includes(s));
        }
        setPharmaList(filtered);
        setTotal(data.total);
      }
    } catch (e) { console.error(e); }
  };

  const handleImportFromURL = async (url) => {
    if (!url || !url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setImporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pharma/import-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted_data;
        
        // Check if we're editing an existing pharma (merge mode) or creating new (replace mode)
        const isEditing = pharmaDialog.pharma !== null;
        
        if (isEditing) {
          // MERGE MODE: Fill empty fields, append to arrays
          const mergeArrays = (existing, newItems) => {
            const existingSet = new Set((existing || []).map(i => i.toLowerCase?.() || JSON.stringify(i)));
            const uniqueNew = (newItems || []).filter(i => !existingSet.has(i.toLowerCase?.() || JSON.stringify(i)));
            return [...(existing || []), ...uniqueNew];
          };
          
          setPharmaForm(prev => ({
            name: prev.name || extracted.name || "",
            headquarters: prev.headquarters || extracted.headquarters || "",
            website: prev.website || extracted.website || url,
            description: prev.description || extracted.description || "",
            indication_focus: mergeArrays(prev.indication_focus, extracted.indication_focus),
            modality_focus: mergeArrays(prev.modality_focus, extracted.modality_focus),
            key_drugs: mergeArrays(prev.key_drugs, extracted.key_drugs),
            pipeline_stage: prev.pipeline_stage || extracted.pipeline_stage || "",
            external_innovation_score: prev.external_innovation_score || extracted.external_innovation_score || 50,
            innovation_approach: prev.innovation_approach || extracted.innovation_approach || "",
            annual_rd_spend: prev.annual_rd_spend || extracted.annual_rd_spend || "",
            recent_acquisitions: mergeArrays(prev.recent_acquisitions, extracted.recent_acquisitions),
            startup_partnerships: [...(prev.startup_partnerships || []), ...(extracted.startup_partnerships || [])],
            best_contact_approach: prev.best_contact_approach || extracted.best_contact_approach || "bd_team",
            contact_notes: prev.contact_notes || extracted.contact_notes || "",
            deal_activity_score: prev.deal_activity_score || extracted.deal_activity_score || 50,
            startup_friendly_score: prev.startup_friendly_score || extracted.startup_friendly_score || 50
          }));
          
          toast.success("Data merged! Empty fields filled and new items added to lists.");
        } else {
          // NEW MODE: Replace all fields
          setPharmaForm({
            name: extracted.name || "",
            headquarters: extracted.headquarters || "",
            website: extracted.website || url,
            description: extracted.description || "",
            indication_focus: extracted.indication_focus || [],
            modality_focus: extracted.modality_focus || [],
            key_drugs: extracted.key_drugs || [],
            pipeline_stage: extracted.pipeline_stage || "",
            external_innovation_score: extracted.external_innovation_score || 50,
            innovation_approach: extracted.innovation_approach || "",
            annual_rd_spend: extracted.annual_rd_spend || "",
            recent_acquisitions: extracted.recent_acquisitions || [],
            startup_partnerships: extracted.startup_partnerships || [],
            best_contact_approach: extracted.best_contact_approach || "bd_team",
            contact_notes: extracted.contact_notes || "",
            deal_activity_score: extracted.deal_activity_score || 50,
            startup_friendly_score: extracted.startup_friendly_score || 50
          });
          toast.success("Data extracted! Review the form below.");
        }
        
        setImportUrl("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to extract data");
      }
    } catch (error) {
      toast.error("Failed to extract data from URL");
    } finally {
      setImporting(false);
    }
  };

  const openPharmaDialog = (pharma = null) => {
    if (pharma) {
      setPharmaForm({
        name: pharma.name || "",
        headquarters: pharma.headquarters || "",
        website: pharma.website || "",
        description: pharma.description || "",
        indication_focus: pharma.indication_focus || [],
        modality_focus: pharma.modality_focus || [],
        key_drugs: pharma.key_drugs || [],
        pipeline_stage: pharma.pipeline_stage || "",
        external_innovation_score: pharma.external_innovation_score || 50,
        innovation_approach: pharma.innovation_approach || "",
        annual_rd_spend: pharma.annual_rd_spend || "",
        recent_acquisitions: pharma.recent_acquisitions || [],
        startup_partnerships: pharma.startup_partnerships || [],
        best_contact_approach: pharma.best_contact_approach || "bd_team",
        contact_notes: pharma.contact_notes || "",
        deal_activity_score: pharma.deal_activity_score || 50,
        startup_friendly_score: pharma.startup_friendly_score || 50
      });
    } else {
      setPharmaForm({
        name: "", headquarters: "", website: "", description: "",
        indication_focus: [], modality_focus: [], key_drugs: [],
        pipeline_stage: "", external_innovation_score: 50, innovation_approach: "",
        annual_rd_spend: "", recent_acquisitions: [], startup_partnerships: [],
        best_contact_approach: "bd_team", contact_notes: "",
        deal_activity_score: 50, startup_friendly_score: 50
      });
    }
    setEditTab("basic");
    setPharmaDialog({ open: true, pharma });
  };

  const savePharma = async () => {
    if (!pharmaForm.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const url = pharmaDialog.pharma 
        ? `${BACKEND_URL}/api/admin/pharma/${pharmaDialog.pharma.pharma_id}`
        : `${BACKEND_URL}/api/admin/pharma`;
      const response = await fetch(url, {
        method: pharmaDialog.pharma ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(pharmaForm)
      });
      if (response.ok) {
        toast.success(pharmaDialog.pharma ? "Updated" : "Created");
        setPharmaDialog({ open: false, pharma: null });
        fetchPharma();
      } else toast.error("Failed to save");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const deletePharma = async () => {
    if (!deleteDialog.pharma) return;
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pharma/${deleteDialog.pharma.pharma_id}`, {
        method: "DELETE", credentials: "include"
      });
      if (response.ok) {
        toast.success("Deleted");
        setDeleteDialog({ open: false, pharma: null });
        fetchPharma();
      } else toast.error("Failed to delete");
    } catch { toast.error("Failed to delete"); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pharma/export`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const headers = Object.keys(data.pharma_companies[0] || {});
        const csvRows = [headers.join(",")];
        for (const p of data.pharma_companies) {
          const row = headers.map(h => {
            let val = p[h] || "";
            if (typeof val === "string" && (val.includes(",") || val.includes('"'))) val = `"${val.replace(/"/g, '""')}"`;
            return val;
          });
          csvRows.push(row.join(","));
        }
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pharma_export_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Exported");
      }
    } catch { toast.error("Export failed"); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result || "";
        const rows = text.split("\n").filter(r => r.trim());
        if (rows.length < 2) { toast.error("Need header + data rows"); return; }
        
        const parseCSVRow = (row) => {
          const result = [];
          let current = "", inQuotes = false;
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"') { if (inQuotes && row[i + 1] === '"') { current += '"'; i++; } else inQuotes = !inQuotes; }
            else if (char === "," && !inQuotes) { result.push(current.trim()); current = ""; }
            else current += char;
          }
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVRow(rows[0]).map(h => h.toLowerCase().replace(/\s+/g, "_"));
        const pharmaData = [];
        for (let i = 1; i < rows.length; i++) {
          const values = parseCSVRow(rows[i]);
          if (values.length < 2) continue;
          const pharma = {};
          headers.forEach((h, idx) => { pharma[h] = values[idx] || ""; });
          pharmaData.push(pharma);
        }
        
        setUploading(true);
        const response = await fetch(`${BACKEND_URL}/api/admin/pharma/bulk-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ pharma_companies: pharmaData })
        });
        if (response.ok) {
          const result = await response.json();
          toast.success(`Created: ${result.created}, Updated: ${result.updated}`);
          fetchPharma();
        } else toast.error("Upload failed");
      } catch { toast.error("Parse failed"); }
      finally { setUploading(false); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const addTag = (field, value) => {
    if (value && !pharmaForm[field].includes(value)) {
      setPharmaForm({ ...pharmaForm, [field]: [...pharmaForm[field], value] });
    }
  };
  const removeTag = (field, value) => {
    setPharmaForm({ ...pharmaForm, [field]: pharmaForm[field].filter(t => t !== value) });
  };

  if (isAdmin === null || loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  
  if (isAdmin === false) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 py-16">
        <Card className="max-w-md mx-auto border-red-200">
          <CardContent className="p-8 text-center">
            <ShieldX className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Manage Pharma Companies</h1>
            <p className="text-slate-600 mt-1">Add, edit, and manage pharma company database</p>
          </div>
        </div>

        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search pharma..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </div>
              <div className="flex gap-3">
                <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileSelect} className="hidden" />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Import CSV
                </Button>
                <Button variant="outline" onClick={handleExport}><Download className="w-4 h-4 mr-2" />Export</Button>
                <Button onClick={() => openPharmaDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />Add Pharma
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="flex items-center gap-2"><Building className="w-5 h-5" />Pharma Companies ({pharmaList.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pharmaList.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No pharma companies found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Company</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Focus</TableHead>
                      <TableHead>Key Drugs</TableHead>
                      <TableHead>Scores</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pharmaList.map((pharma) => (
                      <TableRow key={pharma.pharma_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="font-medium text-slate-900">{pharma.name}</div>
                          <div className="text-xs text-slate-500">{pharma.best_contact_approach}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {pharma.headquarters || "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {pharma.indication_focus?.slice(0, 2).map((ind, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{ind}</Badge>
                            ))}
                            {(pharma.indication_focus?.length || 0) > 2 && <Badge variant="secondary" className="text-xs">+{pharma.indication_focus.length - 2}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Pill className="w-3 h-3 text-slate-400" />
                            {pharma.key_drugs?.length || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs space-y-1">
                            <div>Ext: {pharma.external_innovation_score || 50}%</div>
                            <div>Startup: {pharma.startup_friendly_score || 50}%</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openPharmaDialog(pharma)} className="text-slate-400 hover:text-indigo-600">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, pharma })} className="text-slate-400 hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={pharmaDialog.open} onOpenChange={(open) => setPharmaDialog({ open, pharma: pharmaDialog.pharma })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{pharmaDialog.pharma ? "Edit Pharma Company" : "Add New Pharma Company"}</DialogTitle>
          </DialogHeader>

          {/* URL Import Section - shown for both new and editing */}
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-900">
                {pharmaDialog.pharma ? "Complete from Website" : "Import from Website"}
              </span>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://pharma-company.com"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImportFromURL(importUrl)}
                />
              </div>
              <Button 
                onClick={() => handleImportFromURL(importUrl)} 
                disabled={importing || !importUrl.trim()}
                variant="outline"
                className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                <span className="ml-2">{importing ? "Extracting..." : "Extract"}</span>
              </Button>
            </div>
            <p className="text-xs text-indigo-700 mt-2">
              {pharmaDialog.pharma 
                ? "Fill empty fields and add new drugs, indications, and partnerships (won't overwrite existing data)"
                : "Paste a pharma company URL to auto-fill pipeline, indications, partnerships, and more"
              }
            </p>
          </div>

          <Tabs value={editTab} onValueChange={setEditTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="focus">Focus Areas</TabsTrigger>
              <TabsTrigger value="innovation">Innovation</TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Company Name *</Label><Input value={pharmaForm.name} onChange={(e) => setPharmaForm({...pharmaForm, name: e.target.value})} /></div>
                <div><Label>Headquarters</Label><Input value={pharmaForm.headquarters} onChange={(e) => setPharmaForm({...pharmaForm, headquarters: e.target.value})} placeholder="Basel, Switzerland" /></div>
                <div><Label>Website</Label><Input value={pharmaForm.website} onChange={(e) => setPharmaForm({...pharmaForm, website: e.target.value})} placeholder="https://..." /></div>
              </div>
              <div><Label>Description</Label><Textarea value={pharmaForm.description} onChange={(e) => setPharmaForm({...pharmaForm, description: e.target.value})} rows={3} /></div>
              <div>
                <Label>Key Drugs</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {pharmaForm.key_drugs.map((drug, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">{drug}<X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("key_drugs", drug)} /></Badge>
                  ))}
                </div>
                <Input placeholder="Add drug and press Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag("key_drugs", e.target.value); e.target.value = ""; }}} />
              </div>
            </TabsContent>

            <TabsContent value="focus" className="space-y-4">
              <div>
                <Label>Indication Focus</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {pharmaForm.indication_focus.map((ind, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">{ind}<X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("indication_focus", ind)} /></Badge>
                  ))}
                </div>
                <Select onValueChange={(v) => addTag("indication_focus", v)}>
                  <SelectTrigger><SelectValue placeholder="Add indication..." /></SelectTrigger>
                  <SelectContent>
                    {INDICATION_OPTIONS.filter(i => !pharmaForm.indication_focus.includes(i)).map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modality Focus</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {pharmaForm.modality_focus.map((mod, i) => (
                    <Badge key={i} className="bg-purple-100 text-purple-700 gap-1">{mod}<X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("modality_focus", mod)} /></Badge>
                  ))}
                </div>
                <Select onValueChange={(v) => addTag("modality_focus", v)}>
                  <SelectTrigger><SelectValue placeholder="Add modality..." /></SelectTrigger>
                  <SelectContent>
                    {MODALITY_OPTIONS.filter(m => !pharmaForm.modality_focus.includes(m)).map(mod => (
                      <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="innovation" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>External Innovation Score (0-100)</Label><Input type="number" min="0" max="100" value={pharmaForm.external_innovation_score} onChange={(e) => setPharmaForm({...pharmaForm, external_innovation_score: parseInt(e.target.value) || 0})} /></div>
                <div><Label>Annual R&D Spend</Label><Input value={pharmaForm.annual_rd_spend} onChange={(e) => setPharmaForm({...pharmaForm, annual_rd_spend: e.target.value})} placeholder="$10B+" /></div>
              </div>
              <div><Label>Innovation Approach</Label><Textarea value={pharmaForm.innovation_approach} onChange={(e) => setPharmaForm({...pharmaForm, innovation_approach: e.target.value})} placeholder="How they approach external innovation..." rows={2} /></div>
              <div>
                <Label>Recent Acquisitions</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {pharmaForm.recent_acquisitions.map((acq, i) => (
                    <Badge key={i} className="bg-amber-100 text-amber-700 gap-1">{acq}<X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("recent_acquisitions", acq)} /></Badge>
                  ))}
                </div>
                <Input placeholder="Add acquisition and press Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag("recent_acquisitions", e.target.value); e.target.value = ""; }}} />
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-4">
              <div><Label>Best Contact Approach</Label>
                <Select value={pharmaForm.best_contact_approach} onValueChange={(v) => setPharmaForm({...pharmaForm, best_contact_approach: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTACT_APPROACHES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Contact Notes</Label><Textarea value={pharmaForm.contact_notes} onChange={(e) => setPharmaForm({...pharmaForm, contact_notes: e.target.value})} placeholder="How to best approach this company..." rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Deal Activity Score (0-100)</Label><Input type="number" min="0" max="100" value={pharmaForm.deal_activity_score} onChange={(e) => setPharmaForm({...pharmaForm, deal_activity_score: parseInt(e.target.value) || 0})} /></div>
                <div><Label>Startup Friendly Score (0-100)</Label><Input type="number" min="0" max="100" value={pharmaForm.startup_friendly_score} onChange={(e) => setPharmaForm({...pharmaForm, startup_friendly_score: parseInt(e.target.value) || 0})} /></div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPharmaDialog({ open: false, pharma: null })}>Cancel</Button>
            <Button onClick={savePharma} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {pharmaDialog.pharma ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, pharma: deleteDialog.pharma })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Pharma Company</DialogTitle>
            <DialogDescription>Are you sure you want to delete <strong>{deleteDialog.pharma?.name}</strong>?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, pharma: null })}>Cancel</Button>
            <Button variant="destructive" onClick={deletePharma} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
